import { memo } from "react";

import { ServiceIcon } from "./icons";
import { resolveServiceIconName } from "./service-icon-map";

/**
 * ServiceGridSection: 施設・アメニティサービスのアイコングリッド
 *
 * ゲスト側 app/frontend/guest/_shared/src/components/facility-info/ServiceGridSection.astro の逐語再現。
 * PREVIEW_LIMIT = 4 件表示 + 残りは "N 件" テキスト。
 * アイコンはゲスト共有の service-icon-map.ts + service-icon-registry.ts 経由で解決する。
 */

type ServiceItem = { key: string; label: string };
type ServiceGroup = {
	title: string;
	category: "amenity" | "facility";
	categoriesText: string;
	items: ServiceItem[];
};

const PREVIEW_LIMIT = 4;

const DEFAULT_GROUPS: ServiceGroup[] = [
	{
		title: "お部屋のアメニティ",
		category: "amenity",
		categoriesText: "基本設備 / バスルーム / 備品",
		items: [
			{ key: "wifi", label: "Wi-Fi" },
			{ key: "tv", label: "テレビ" },
			{ key: "fridge", label: "冷蔵庫" },
			{ key: "bathtub", label: "バスタブ" },
			{ key: "shower", label: "シャワー" },
			{ key: "dryer", label: "ドライヤー" },
		],
	},
	{
		title: "施設・共有スペース",
		category: "facility",
		categoriesText: "飲食 / 共用設備 / 交通",
		items: [
			{ key: "cafe", label: "カフェ" },
			{ key: "pool", label: "プール" },
			{ key: "parking_lot", label: "駐車場" },
			{ key: "hot_spring", label: "大浴場" },
			{ key: "lounge", label: "ラウンジ" },
		],
	},
];

type Props = {
	groups?: ServiceGroup[];
};

export const ServiceGridSection = memo(({ groups = DEFAULT_GROUPS }: Props) => {
	return (
		<div>
			<h3 className="mb-5 text-xl font-semibold text-zinc-800">施設・設備</h3>
			<div className="space-y-4">
				{groups.map((group, groupIdx) => {
					const previewItems = group.items.slice(0, PREVIEW_LIMIT);
					const remainingCount = Math.max(group.items.length - previewItems.length, 0);
					return (
						<section key={groupIdx} className="rounded-2xl bg-zinc-50 p-4">
							<h4 className="text-base font-semibold text-zinc-800">{group.title}</h4>
							<p className="mt-1 text-sm text-zinc-500">{group.categoriesText}</p>
							<div className="mt-3 grid grid-cols-1 gap-3.5 md:grid-cols-2">
								{previewItems.map((item) => (
									<div key={item.key} className="flex items-center gap-2">
										<ServiceIcon
											iconName={resolveServiceIconName(item.key, group.category)}
											size={24}
											className="shrink-0 text-zinc-800"
										/>
										<span className="text-base font-normal text-zinc-800">{item.label}</span>
									</div>
								))}
								{remainingCount > 0 && (
									<div className="pt-1 text-sm text-zinc-500">他 {remainingCount} 件</div>
								)}
							</div>
						</section>
					);
				})}
			</div>
			<div className="mt-5">
				<button
					type="button"
					className="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
				>
					すべての設備・サービスを見る
				</button>
			</div>
		</div>
	);
});

ServiceGridSection.displayName = "ServiceGridSection";
